import { ButtonVariant } from "@amzn/stencil-react-components/button";
import { FormWrapper } from "@amzn/stencil-react-components/form";
import { InputWrapper } from "@amzn/stencil-react-components/input";
import { Col, GridContainer, GridItem, Row } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import { Button } from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import { Input } from "@amzn/uxm-stencil-react-components/lib/src/stencil/input";
import { Select as UxmSelect } from "@amzn/uxm-stencil-react-components/lib/src/stencil/select";
import { TextArea } from "@amzn/uxm-stencil-react-components/lib/src/stencil/text-area";
import { Paths } from "common/paths/paths";
import { getString } from "common/uistringlabels/uiStringUtils";
import React, { FC, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import {
  AiForMeFormData,
  BINDLE_PATTERN,
  DataClassification,
  TOOL_MATURITY_OPTIONS,
  ToolMaturity,
} from "./constants/platform-constants";

export interface AiForMeThirdPartyDetailsProps {
  formData: AiForMeFormData;
  onNext: (data: Partial<AiForMeFormData>) => void;
  onBack: (data?: Partial<AiForMeFormData>) => void;
}

export const AiForMeThirdPartyDetails: FC<AiForMeThirdPartyDetailsProps> = ({ formData, onNext, onBack }) => {
  const history = useHistory();

  const [vendorName, setVendorName] = useState<string>("");
  const [businessJustification, setBusinessJustification] = useState<string>("");
  const [toolBindle, setToolBindle] = useState<string>("");
  const [toolMaturity, setToolMaturity] = useState<ToolMaturity>(ToolMaturity.PRODUCTION);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Pre-populate form data if available (from back button on review page)
  useEffect(() => {
    if (formData) {
      setVendorName(formData.vendorName || "");
      setBusinessJustification(formData.businessJustification || "");
      setToolBindle(formData.toolBindle || "");
      setToolMaturity(formData.toolMaturity || ToolMaturity.PRODUCTION);
    }
  }, [formData]);

  const handleSubmit = (e: React.FormEvent<HTMLElement>) => {
    e.preventDefault();

    const updatedData = {
      ...formData,
      vendorName,
      businessJustification,
      toolBindle,
      toolMaturity,
      dataClassification: DataClassification.UNKNOWN, // Always set to "Unknown" for third-party tools
    };

    onNext(updatedData);
  };

  const handleBackClick = () => {
    const currentData = {
      ...formData,
      vendorName,
      businessJustification,
      toolBindle,
      toolMaturity,
      dataClassification: DataClassification.UNKNOWN,
    };
    onBack(currentData);
  };

  return (
    <GridContainer>
      <GridItem lg={3} md={2}>
        &nbsp;
      </GridItem>
      <GridItem lg={6} md={4}>
        <form onSubmit={handleSubmit} style={{ width: "100%" }}>
          <FormWrapper width={"100%"}>
            <Col rowGap={"30px"}>
              <Text fontSize="T400" fontWeight="bold" data-cy="third-party-details-title">
                Third-Party Tool Details
              </Text>
              <Text fontSize="T100" data-cy="third-party-details-subtitle">
                Please provide additional information about your third-party tool.
              </Text>

              <InputWrapper labelText={getString("aiForMe.formVendorName")} id="vendor-name">
                {(inputProps) => (
                  <Input
                    placeholder="Enter the vendor name"
                    required
                    value={vendorName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVendorName(e.target.value)}
                    data-cy="vendor-name"
                    touchpoint="third-party-vendor-name"
                    {...inputProps}
                  />
                )}
              </InputWrapper>

              <InputWrapper
                labelText={getString("aiForMe.formBusinessJustificationRequired")}
                id="business-justification"
              >
                {(inputProps) => (
                  <TextArea
                    placeholder="Enter the business justification for using this third-party tool"
                    required
                    value={businessJustification}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setBusinessJustification(e.target.value)}
                    data-cy="business-justification"
                    touchpoint="third-party-business-justification"
                    rows={4}
                    {...inputProps}
                  />
                )}
              </InputWrapper>

              <InputWrapper labelText={getString("aiForMe.formToolMaturity")} id="tool-maturity">
                {(inputProps) => (
                  <UxmSelect
                    options={TOOL_MATURITY_OPTIONS}
                    value={toolMaturity}
                    onChange={(option: ToolMaturity | undefined) => option && setToolMaturity(option)}
                    data-cy="third-party-tool-maturity"
                    defaultValue={ToolMaturity.PRODUCTION}
                    touchpoint="third-party-tool-maturity"
                    {...inputProps}
                  />
                )}
              </InputWrapper>

              <InputWrapper
                labelText={getString("aiForMe.intakeToolBindle")}
                id="tool-bindle"
                footer="Enter a valid bindle (e.g. amzn1.bindle.resource.abc123)"
              >
                {(inputProps) => (
                  <Input
                    placeholder="Enter team bindle"
                    pattern={BINDLE_PATTERN}
                    value={toolBindle}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setToolBindle(e.target.value)}
                    data-cy="tool-bindle"
                    touchpoint="third-party-tool-bindle"
                    {...inputProps}
                  />
                )}
              </InputWrapper>

              <Row justifyContent="space-between" columnGap={5}>
                <Button
                  variant={ButtonVariant.Secondary}
                  onClick={() => history.push(Paths.aiforme.url)}
                  type="button"
                  data-cy="third-party-cancel-button"
                  touchpoint="third-party-cancel"
                >
                  {getString("aiForMe.cancel")}
                </Button>
                <Row columnGap={5}>
                  <Button
                    variant={ButtonVariant.Secondary}
                    onClick={handleBackClick}
                    type="button"
                    data-cy="third-party-back-button"
                    touchpoint="third-party-back"
                  >
                    {getString("aiForMe.previousPage")}
                  </Button>
                  <Button
                    variant={ButtonVariant.Primary}
                    type="submit"
                    data-cy="third-party-next-button"
                    touchpoint="third-party-next"
                  >
                    {getString("aiForMe.nextPage")}
                  </Button>
                </Row>
              </Row>
            </Col>
          </FormWrapper>
        </form>
      </GridItem>
      <GridItem lg={3} md={2}>
        &nbsp;
      </GridItem>
    </GridContainer>
  );
};
